import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { ArrowRight, ArrowLeft, Building2, Mail, Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import TenantUnavailable from "../components/TenantUnavailable";

export default function EntrarPage() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [step, setStep] = useState("email"); // email | select | password
  const [email, setEmail] = useState("");
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/lookup/tenants", { email: email.trim().toLowerCase() });
      const list = data.tenants || [];
      if (list.length === 0) {
        toast.error("Não encontramos nenhum estabelecimento com esse e-mail.");
      } else if (list.length === 1) {
        const only = list[0];
        setSelected(only);
        setTenants(list);
        if (only.active === false) {
          // Tenant único mas inativo: mantém tela institucional aqui.
          setStep("inactive");
        } else {
          // Reutiliza a tela de login personalizada do tenant (mesma que aparece
          // pós-logout) para preservar identidade + card de instalar PWA.
          nav(`/${only.slug}/login`, { state: { email: email.trim().toLowerCase() } });
        }
      } else {
        setTenants(list);
        setStep("select");
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const chooseTenant = (t) => {
    setSelected(t);
    if (t.active === false) {
      setStep("inactive");
    } else {
      nav(`/${t.slug}/login`, { state: { email: email.trim().toLowerCase() } });
    }
  };

  const submitPassword = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      // Override header X-Tenant-Slug para o slug escolhido no fluxo /entrar
      const { data } = await api.post("/auth/login",
        { email: email.trim().toLowerCase(), password },
        { headers: { "X-Tenant-Slug": selected.slug } });
      localStorage.setItem("flr_token", data.token);
      // dispara o refresh do AuthContext
      window.dispatchEvent(new Event("branding-updated"));
      // navegação — chama login() de novo pra sincronizar o context
      const r = await login(email.trim().toLowerCase(), password);
      if (r.ok) {
        const home = r.user.role === "admin" ? "fila" : "minha-vez";
        nav(`/${selected.slug}/${home}`);
      } else {
        toast.error(r.error || "Credenciais inválidas");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-10 relative overflow-hidden">
      {/* Backdrop pink blobs */}
      <div className="pointer-events-none absolute -top-32 -right-24 h-96 w-96 rounded-full bg-brand-100/60 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-24 h-96 w-96 rounded-full bg-brand-50 blur-3xl" />

      <div className="w-full max-w-md relative">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <img src="/fila-gestao-logo.png?v=2" alt="FILA Gestão" className="h-20 w-20 object-contain drop-shadow" />
          <h1 className="font-display text-3xl font-semibold tracking-tight">FILA <span className="text-brand-500">Gestão</span></h1>
          <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Gestão da vez</p>
        </div>

        {step === "email" && (
          <form onSubmit={submitEmail} data-testid="entrar-email-form" className="rounded-2xl border border-brand-100 bg-white p-6 sm:p-8 shadow-lg shadow-brand-500/5">
            <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-2">Seu e-mail</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input
                data-testid="entrar-email-input"
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                autoComplete="email"
                placeholder="voce@estabelecimento.com"
                className="w-full rounded-lg border border-zinc-200 bg-white pl-10 pr-4 py-3 text-base outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="entrar-email-submit"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 py-3.5 text-sm font-semibold uppercase tracking-widest text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 disabled:opacity-60 transition-all"
            >
              {busy ? "Buscando…" : "Continuar"} {!busy && <ArrowRight size={14} />}
            </button>
            <p className="mt-4 text-center text-[11px] text-zinc-500">
              Também é possível acessar direto por <code className="text-brand-600">filagestao.com.br/&lt;slug&gt;</code>
            </p>
          </form>
        )}

        {step === "select" && (
          <div className="rounded-2xl border border-brand-100 bg-white p-6 sm:p-8 shadow-lg shadow-brand-500/5" data-testid="entrar-select-tenant">
            <button onClick={() => { setStep("email"); setTenants([]); }} className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-brand-600 mb-4">
              <ArrowLeft size={12} /> Trocar e-mail
            </button>
            <h2 className="text-lg font-semibold">Selecione o estabelecimento</h2>
            <p className="text-sm text-zinc-500 mb-6">O e-mail <strong>{email}</strong> está em mais de um estabelecimento.</p>
            <ul className="space-y-3">
              {tenants.map((t) => (
                <li key={t.slug}>
                  <button
                    type="button"
                    data-testid={`entrar-tenant-${t.slug}`}
                    onClick={() => chooseTenant(t)}
                    disabled={false}
                    className={`w-full flex items-center gap-4 rounded-xl border p-4 text-left transition-colors ${
                      t.active === false
                        ? "border-zinc-200 bg-zinc-50/60 hover:border-zinc-300"
                        : "border-zinc-200 bg-white hover:border-brand-500 hover:bg-brand-50/40"
                    }`}
                  >
                    {t.logo_url ? (
                      <img src={t.logo_url} alt={t.name} className={`h-11 w-11 rounded-full object-cover border border-zinc-200 ${t.active === false ? "opacity-70 grayscale" : ""}`} />
                    ) : (
                      <div className="h-11 w-11 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500"><Building2 size={18} /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{t.display_name || t.name}</div>
                      <div className="text-xs text-zinc-500">filagestao.com.br/{t.slug}</div>
                      {t.active === false && (
                        <div
                          data-testid={`entrar-tenant-${t.slug}-unavailable`}
                          className="mt-1 inline-flex items-center rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-500"
                        >
                          Temporariamente indisponível
                        </div>
                      )}
                    </div>
                    <ArrowRight size={16} className="ml-auto text-zinc-400" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {step === "inactive" && selected && (
          <TenantUnavailable
            tenant={selected}
            testidPrefix="entrar-inactive"
            onBack={() => setStep(tenants.length > 1 ? "select" : "email")}
          />
        )}

        {step === "password" && selected && (
          <form onSubmit={submitPassword} data-testid="entrar-password-form" className="rounded-2xl border border-brand-100 bg-white p-6 sm:p-8 shadow-lg shadow-brand-500/5">
            <button type="button" onClick={() => { setStep(tenants.length > 1 ? "select" : "email"); setPassword(""); }} className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-brand-600 mb-4">
              <ArrowLeft size={12} /> Voltar
            </button>
            <div className="flex flex-col items-center gap-3 mb-6 pb-6 border-b border-zinc-100 text-center">
              {selected.logo_url ? (
                <img src={selected.logo_url} alt={selected.name} data-testid="entrar-selected-logo" className="h-20 w-20 rounded-full object-cover border-2 border-brand-100 shadow-sm" />
              ) : (
                <div className="h-20 w-20 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-500 border-2 border-brand-100"><Building2 size={28} /></div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Entrando em</div>
                <div className="font-display text-xl font-semibold" data-testid="entrar-selected-name">
                  {selected.display_name || selected.name}
                </div>
                {selected.segment && (
                  <div className="text-[10px] uppercase tracking-[0.2em] text-brand-500 mt-1" data-testid="entrar-selected-segment">{selected.segment}</div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-2">E-mail</label>
              <input type="email" value={email} readOnly data-testid="entrar-email-readonly" className="w-full rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-base text-zinc-600" />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-zinc-500 mb-2">Senha</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  data-testid="entrar-password-input"
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  required autoFocus autoComplete="current-password"
                  className="w-full rounded-lg border border-zinc-200 bg-white pl-10 pr-4 py-3 text-base outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="entrar-password-submit"
              className="mt-6 w-full rounded-full bg-brand-500 py-3.5 text-sm font-semibold uppercase tracking-widest text-white shadow-sm shadow-brand-500/20 hover:bg-brand-600 disabled:opacity-60 transition-all"
            >
              {busy ? "Entrando…" : `Entrar em ${selected.display_name || selected.name}`}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-[11px] text-zinc-400">
          <a href="/" className="hover:text-brand-600">← Voltar para FILA Gestão</a>
        </p>
      </div>
    </div>
  );
}
